/*
 * Data Assistant Store
 * */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useApi } from '@/hooks/useApi';

export const useDataAssistantStore = defineStore('dataAssistant', () => {
  const api = useApi();

  const floating = ref(true);

  const fullScreen = ref(false);

  const printLayout = ref(false);

  const modelName = ref('o4-mini');

  // data to analyze, object or array of objects
  const data = ref(null);

  // additional instructions to be passed to LLM
  const additionalInstructions = ref(null);

  // short title of the data to use in the labels
  const dataTitle = ref(null);

  // description of data - what fields it contains, what each field means
  const dataDescription = ref(null);

  // data label - to show in the UI, e.g. "Customer Satisfaction Data"
  const dataLabel = ref(null);

  // List of suggested questions
  const suggestedQuestions = ref([]);

  // ???
  // return data spec - if passed, return result as JSON according to passed spec
  // must be in the tool call function parameters spec
  const returnDataSpec = ref(null);

  // Previous messages in data analysis conversation.
  // User may ask multiple questions, gradually clarifying what is needed.
  const messages = ref([]);

  // Is data assistant visible or not
  const visible = ref(false);

  // Width of the assistant
  const width = ref(600);

  // Visible width of the assistant
  const visibleWidth = computed(() => (visible.value ? width.value : 0));

  // Is data assistant thinking or not
  const thinking = ref(false);

  const inputLabel = ref('How can I help?');
  const inputDescription = computed(() => {
    return `I can analyze ${dataTitle.value ? dataTitle.value : 'this data'} and answer questions`;
  });
  const inputPlaceholder = ref('Ask me questions about data ...');
  const suggestions = ref([]);

  // Initialize and show assistant
  function init({
    assistantData = null,
    assistantDataTitle = null,
    assistantDataDescription = null,
    assistantSuggestedQuestions = null,
    assistantReturnDataSpec = null,
    assistantMessages = [],
    show = true,
    float = false,
    initialWidth = 600,
  }) {
    dataTitle.value = assistantDataTitle;
    dataDescription.value = assistantDataDescription;
    returnDataSpec.value = assistantReturnDataSpec;
    suggestedQuestions.value = Array.isArray(assistantSuggestedQuestions) ? assistantSuggestedQuestions : [];
    messages.value = assistantMessages || [];
    width.value = initialWidth;
    floating.value = float;

    //```mermaid\nxychart-beta\n    title \"Average CSat by Query Type\"\n    x-axis [\"Maintenance\",\"DLP Definition\",\"Agent Connect\",\"Greeting\"]\n    y-axis \"CSat Score\" 0 --> 10\n    bar [7.5,6.5,3,5]\n```

    /*
    messages.value = [
      {
        role: 'user',
        content: 'test',
      },
      {
        role: 'assistant',
        content:
          '# Here we are testing mermaid\n\n' +
          '```mermaid\nxychart-beta\n    title "Average CSat by Query Type"\n    x-axis ["Maintenance","DLP Definition","Agent Connect","Greeting"]\n    y-axis "CSat Score" 0 --> 10\n    bar [7.5,6.5,3,5]\n```\n\n' +
          'so how does it look?',
      },
    ];
    */

    /*
    messages.value = [
      {
        role: 'user',
        content: 'test',
      },
      {
        role: 'assistant',
        content: '# Here we are testing mermaid\n\n' + '```mermaid\n' + 'pie title Pets adopted by volunteers\n' + '    "Dogs" : 386\n' + '    "Cats" : 85\n' + '    "Rats" : 15\n' + '```\n\n' + 'so how does it look?',
      },
      {
        role: 'assistant',
        content:
          '# Here we are testing mermaid 1\n\n' +
          'Here’s how the 14 sampled conversations break down by topic:\n' +
          '\n' +
          'Pie Chart of Conversation Topics\n' +
          '```mermaid\n' +
          'pie title Conversation Topics\n' +
          '  "DLP Maintenance Request" : 4\n' +
          '  "DLP Definition": 3\n' +
          '```\n',
      },
      {
        role: 'assistant',
        content:
          '# Here we are testing mermaid 2\n\n' +
          '```mermaid\n' +
          'xychart-beta\n' +
          '    title "Sales Revenue"\n' +
          '    x-axis [jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec]\n' +
          '    y-axis "Revenue (in $)" 4000 --> 11000\n' +
          '    bar [5000, 6000, 7500, 8200, 9500, 10500, 11000, 10200, 9200, 8500, 7000, 6000]\n' +
          '    line [5000, 6000, 7500, 8200, 9500, 10500, 11000, 10200, 9200, 8500, 7000, 6000]\n' +
          '```\n',
      },
      {
        role: 'assistant',
        content:
          '# Here we are testing mermaid 3\n\n' +
          '```mermaid\n' +
          'xychart-beta\n' +
          '    title "Sales Revenue"\n' +
          '    x-axis [jan, feb, mar, apr, may, jun, jul, aug, sep, oct, nov, dec]\n' +
          '    y-axis "Revenue (in $)" 4000 --> 11000\n' +
          '    bar [5000, 6000, 7500, 8200, 9500, 10500, 11000, 10200, 9200, 8500, 7000, 6000]\n' +
          '```\n',
      },
    ];
    */

    setData(assistantData);
    visible.value = show;
  }

  function setDataInfo({ description = null, title = null, label = null } = {}) {
    if (description) {
      dataDescription.value = description;
    }
    if (title) {
      dataTitle.value = title;
    }
    if (label) {
      dataLabel.value = label;
    }
  }

  function setWidth(w) {
    width.value = w;
  }

  function setData(assistantData = null) {
    data.value = assistantData;
  }

  function setModel(model) {
    modelName.value = model;
  }

  function show() {
    visible.value = true;
  }

  function hide() {
    visible.value = false;
  }

  async function ask(question) {
    messages.value.push({
      role: 'user',
      content: question,
    });

    /* testing
    messages.value.push({
      role: 'assistant',
      content:
        '# Here we are testing mermaid\n\n' +
        '```mermaid\n' +
        'graph TD\n' +
        '    A[Christmas] -->|Get money| B(Go shopping)\n' +
        '    B --> C{Let me think}\n' +
        '    C -->|One| D[Laptop]\n' +
        '    C -->|Two| E[iPhone]\n' +
        '    C -->|Three| F[Car]\n' +
        '```\n\n' +
        'so how does it look?',
    });
    return;
    */

    thinking.value = true;

    const res = await api.executeDataProcessor({
      modelName: modelName.value,
      data: data.value,
      dataDescription: dataDescription.value,
      //returnDataSpec: returnDataSpec.value, // ???
      question: question,
      messages: messages.value,
    });

    thinking.value = false;

    if (res.success) {
      messages.value.push({
        role: 'assistant',
        content: res?.data?.text,
      });
    }

    return res.success;
  }

  function clearConversation() {
    messages.value = [];
  }

  return {
    floating,
    fullScreen,
    printLayout,
    modelName,
    visible,
    width,
    visibleWidth,
    thinking,
    data,
    dataTitle,
    dataDescription,
    dataLabel,
    additionalInstructions,
    suggestedQuestions,
    messages,
    inputLabel,
    inputDescription,
    inputPlaceholder,
    suggestions,
    init,
    setDataInfo,
    setWidth,
    setData,
    setModel,
    show,
    hide,
    ask,
    clearConversation,
  };
});
